/**
 * OTIF day-counting rule (METRIC-DEFINITIONS Q2).
 *
 * Toggles how the OTIF tolerance window counts days for this tenant: plain
 * calendar days (default) or the legacy platform's non-Sunday walk, for
 * customers migrating from it whose historical verdicts were produced that
 * way. Forward-only: flipping it re-verdicts nothing by itself — history is
 * rewritten only by the explicit Recalculate Delivery Metrics admin action.
 * Reads/writes SupplierPerformanceSettings.otif_skip_sundays.
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Loader2, CalendarOff } from 'lucide-react';
import { performanceSettingsAPI } from '../../../lib/api';
import { toast } from 'sonner';

export function OtifSundayRuleSection() {
  const [loading, setLoading] = useState(true);
  const [skipSundays, setSkipSundays] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await performanceSettingsAPI.getSettings();
        setSkipSundays(!!data.otif_skip_sundays);
      } catch (err: unknown) {
        console.error('Error loading OTIF day-counting setting:', err);
        toast.error('Failed to load the OTIF day-counting setting');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleToggle = async (checked: boolean) => {
    const previous = skipSundays;
    setSkipSundays(checked);
    setSaving(true);
    try {
      const { data } = await performanceSettingsAPI.updateSettings({
        otif_skip_sundays: checked,
      });
      setSkipSundays(!!data.otif_skip_sundays);
      toast.success(
        checked
          ? 'OTIF tolerance days now skip Sundays (new verdicts only)'
          : 'OTIF tolerance days now count calendar days (new verdicts only)',
      );
    } catch (err: unknown) {
      console.error('Error saving OTIF day-counting setting:', err);
      toast.error('Failed to save the OTIF day-counting setting');
      setSkipSundays(previous);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarOff className="h-5 w-5" />
          OTIF Day Counting
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          How the OTIF tolerance window counts days when judging deliveries.
        </p>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between rounded-md border border-input bg-background px-4 py-3">
          <div className="space-y-1 pr-3">
            <Label htmlFor="otif-skip-sundays" className="text-sm font-medium">
              Skip Sundays in the tolerance window
            </Label>
            <p className="text-xs text-muted-foreground">
              When on, Sundays never use up a tolerance day (matches the previous
              platform&apos;s behaviour). Applies to newly judged deliveries only —
              existing verdicts change only when Recalculate Delivery Metrics is run.
            </p>
          </div>
          <Switch
            id="otif-skip-sundays"
            checked={skipSundays}
            disabled={saving}
            onCheckedChange={handleToggle}
          />
        </div>
      </CardContent>
    </Card>
  );
}
